import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  CircularProgress,
  Chip,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import LeagueTeams from "./components/LeagueTeams";
import LeagueSchedule from "./components/LeagueSchedule";
import LeagueStandings from "./components/LeagueStandings";
import LeagueSettings from "./components/LeagueSettings";
import LeagueComms from "./components/LeagueComms";
import PlayerList from "./components/PlayerList";
import request from "../../utilities/requests";
import dayjs from "dayjs";

const LeagueController = () => {
  const { id: eventId } = useParams();
  const [leagueEvent, setLeagueEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("1");

  useEffect(() => {
    if (eventId) {
      loadLeagueEvent();
    }
  }, [eventId]);

  const loadLeagueEvent = async () => {
    setLoading(true);
    const result = await request(`leagueevent?eventId=${eventId}`);
    if (result?.success) {
      setLeagueEvent(result.data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!leagueEvent) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">League event not found.</Typography>
      </Box>
    );
  }

  const event = leagueEvent.event;

  return (
    <Box>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">{event?.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {(() => {
                const parts = [];
                if (event?.start || event?.end) {
                  const s = event.start ? dayjs(event.start).format("MMM D") : "";
                  const e = event.end ? dayjs(event.end).format("MMM D, YYYY") : "";
                  parts.push(s && e ? `${s} – ${e}` : s || e);
                }
                if (leagueEvent.sportType) parts.push(leagueEvent.sportType);
                if (event?.venueName) parts.push(event.venueName);
                return parts.join(" · ");
              })()}
            </Typography>
          </Box>
          <Chip
            label={leagueEvent.standingsMode === "realtime" ? "Live Standings" : "Manual Standings"}
            size="small"
            color={leagueEvent.standingsMode === "realtime" ? "success" : "default"}
            variant="outlined"
          />
        </Toolbar>
      </AppBar>

      <TabContext value={tab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Players" value="1" />
            <Tab label="Teams & Rosters" value="2" />
            <Tab label="Schedule" value="3" />
            <Tab label="Standings" value="4" />
            <Tab label="Comms" value="5" />
            <Tab label="Settings" value="6" />
          </Tabs>
        </Box>

        <TabPanel value="1" sx={{ px: { xs: 0.5, sm: 3 }, py: 3 }}>
          <PlayerList eventId={eventId} leagueEvent={leagueEvent} onLeagueEventUpdate={setLeagueEvent} />
        </TabPanel>

        <TabPanel value="2" sx={{ p: 0 }}>
          <LeagueTeams
            eventId={eventId}
            leagueEvent={leagueEvent}
            onLeagueEventUpdate={setLeagueEvent}
          />
        </TabPanel>

        <TabPanel value="3" sx={{ p: 0 }}>
          <LeagueSchedule
            eventId={eventId}
            leagueEvent={leagueEvent}
          />
        </TabPanel>

        <TabPanel value="4" sx={{ p: 0 }}>
          <LeagueStandings
            leagueEvent={leagueEvent}
            onLeagueEventUpdate={setLeagueEvent}
          />
        </TabPanel>

        <TabPanel value="5" sx={{ p: 0 }}>
          <LeagueComms
            eventId={eventId}
            leagueEvent={leagueEvent}
          />
        </TabPanel>

        <TabPanel value="6" sx={{ p: 0 }}>
          <LeagueSettings
            leagueEvent={leagueEvent}
            onLeagueEventUpdate={setLeagueEvent}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default LeagueController;
